import { FC } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { KeywordDetail } from 'src/pages/KeywordDetail'
import {
  AD_ANALYSIS_LIBRARY_ROUTE,
  AD_ANALYSIS_OVERVIEW_ROUTE,
  AD_ANALYSIS_PERFORMANCE_ROUTE,
  AI_ASSISTANT_ROUTE,
  AI_LISTENING_BRAND_NEW_ROUTE,
  AI_LISTENING_BRAND_PROFILE_ROUTE,
  AI_LISTENING_BRAND_ROUTE,
  AI_LISTENING_CAMPAIGN_DETAIL_ROUTE,
  AI_LISTENING_COMPETITORS_ROUTE,
  AI_LISTENING_COMPETITOR_NEW_ROUTE,
  AI_LISTENING_COMPETITOR_ROUTE,
  AI_LISTENING_DETAILS_ROUTE,
  AI_LISTENING_NEW_CAMPAIGN_ROUTE,
  AI_LISTENING_PROMPTS_ROUTE,
  AI_LISTENING_PROMPT_DETAILS_ROUTE,
  AI_LISTENING_ROUTE,
  CAMPAIGNS_ROUTE,
  CAMPAIGN_DETAILS_ROUTE,
  CONTENT_ANALYSIS_DETAIL_ROUTE,
  CONTENT_ANALYSIS_ROUTE,
  CUSTOM_REPORTING_ROUTE,
  HOME_ROUTE,
  INBOX_ANALYSIS_DETAIL_ROUTE,
  INBOX_ANALYSIS_ROUTE,
  KEYWORDS_ROUTE,
  KEYWORD_DETAILS_ROUTE,
  LOGIN_ROUTE,
  NEW_CAMPAIGN_ROUTE,
  PROJECTS_ROUTE,
  REVIEW_ANALYSIS_BRAND_LEVEL_WIDGETS_ROUTE,
  REVIEW_ANALYSIS_CAMPAIGN_ROUTE,
  REVIEW_ANALYSIS_CONNECT_ROUTE,
  REVIEW_ANALYSIS_PLATFORM_DETAILS_ROUTE,
  REVIEW_ANALYSIS_FACILITY_DETAILS_ROUTE,
  REVIEW_ANALYSIS_ROUTE,
  SETTINGS_ACCOUNT_ROUTE,
  SETTINGS_BILLING_ROUTE,
  SETTINGS_COMPANY_PROFILE_ROUTE,
  SETTINGS_ROUTE,
  SETTINGS_SUBSCRIPTION_ROUTE,
  SIGNUP_ROUTE,
  SMART_INSIGHTS_TEST_ROUTE,
  SUBSCRIPTION_CANCEL_ROUTE,
  SUBSCRIPTION_SUCCESS_ROUTE,
  THREADS_ROUTE,
  UPDATE_CAMPAIGN_ROUTE,
} from 'src/constants/routes'
import { Campaigns } from 'src/pages/Campaigns'
import SignUp from 'src/pages/SignUp'
import { HomePage } from 'src/pages/HomePage'
import { ProjectsComingSoonPage } from 'src/pages/ProjectsComingSoon'
import { InboxAnalysis } from 'src/pages/InboxAnalysis'
import { InboxAnalysisDetail } from 'src/pages/InboxAnalysisDetail'
import { ContentAnalysis } from 'src/pages/ContentAnalysis'
import { ContentAnalysisDetail } from 'src/pages/ContentAnalysisDetail'
import { ReviewAnalysis } from 'src/pages/ReviewAnalysis'
import { BrandLevelWidgets } from 'src/pages/ReviewAnalysis/BrandLevelWidgets'
import { CampaignSelector } from 'src/pages/ReviewAnalysis/CampaignSelector'
import { PlatformLevelWidgets } from 'src/pages/ReviewAnalysis/PlatformLevelWidgets'
import { FacilityLevelWidgets } from 'src/pages/ReviewAnalysis/FacilityLevelWidgets'
import { ConnectPlatformsPage } from 'src/pages/ReviewAnalysis/ConnectPlatforms/ConnectPlatformsPage'
import { AdAnalysisOverview } from 'src/pages/AdAnalysis/Overview'
import { AdLibrary } from 'src/pages/AdAnalysis/AdLibrary'
import { AdLibraryDetails } from 'src/pages/AdAnalysis/AdLibrary/Details'
import { AdPerformance } from 'src/pages/AdAnalysis/AdPerformance'
import { CustomReporting } from 'src/pages/CustomReporting'
import { AddNewCampaign } from 'src/pages/AddNewCampaign'
import UpdateTheme from 'src/pages/EditCampaign'
import { KeywordsPage } from 'src/pages/KeywordsPage'
import { Settings } from 'src/pages/Settings'
import ThreadsPage from 'src/pages/Threads'
import { AIAssistantPage } from 'src/pages/AIAssistant'
import {
  BrandProfileLanding,
  BrandProfilePage,
  CreateBrandProfilePage,
} from 'src/pages/AIListening/BrandProfile'
import {
  CompetitorsPage,
  CreateCompetitorPage,
} from 'src/pages/AIListening/Competitors'
import {
  AiListeningCampaignRedirect,
  AiListeningHomeRedirect,
  AiListeningNewCampaignRedirect,
} from 'src/pages/AIListening/LegacyRedirects'
import { PromptDeepDivePage } from 'src/pages/AIListening/PromptDeepDive'
import { PromptsPage } from 'src/pages/AIListening/Prompts'
import { Login } from 'src/pages/Login'
import ThemeDetail from 'src/pages/ThemeDetail'
import { SmartInsightsTest } from 'src/pages/SmartInsightsTest'
import { AIAssistantProvider } from 'src/contexts/AIAssistantContext'
import { ActivityTracker } from 'src/components/ActivityTracker'
import { AiListeningRoute } from './AiListeningRoute'
import { PrivateRoute } from './PrivateRoute'
import { ScrollToTop } from './ScrollToTop'

export const AppRouter: FC = () => {
  return (
    <AIAssistantProvider>
      <BrowserRouter>
        <ScrollToTop />
        {/* Inside the router and outside <Routes>: covers every route, /login included. */}
        <ActivityTracker />
        <Routes>
          <Route
            element={<PrivateRoute requiresAdminAccess />}
            path={THREADS_ROUTE}
          >
            <Route element={<ThreadsPage />} path={THREADS_ROUTE} />
          </Route>

          <Route element={<PrivateRoute />} path={UPDATE_CAMPAIGN_ROUTE}>
            <Route element={<UpdateTheme />} path={UPDATE_CAMPAIGN_ROUTE} />
          </Route>

          <Route element={<PrivateRoute />} path={CAMPAIGN_DETAILS_ROUTE}>
            <Route element={<ThemeDetail />} path={CAMPAIGN_DETAILS_ROUTE} />
          </Route>

          <Route element={<PrivateRoute />} path={KEYWORD_DETAILS_ROUTE}>
            <Route element={<KeywordDetail />} path={KEYWORD_DETAILS_ROUTE} />
          </Route>

          <Route element={<PrivateRoute />} path={CAMPAIGNS_ROUTE}>
            <Route element={<Campaigns />} path={CAMPAIGNS_ROUTE} />
          </Route>

          <Route element={<PrivateRoute />} path={HOME_ROUTE}>
            <Route element={<HomePage />} path={HOME_ROUTE} />
          </Route>

          <Route element={<PrivateRoute />} path={PROJECTS_ROUTE}>
            <Route element={<ProjectsComingSoonPage />} path={PROJECTS_ROUTE} />
          </Route>

          <Route
            element={<PrivateRoute />}
            path={`${PROJECTS_ROUTE}/:projectId`}
          >
            <Route
              element={<ProjectsComingSoonPage />}
              path={`${PROJECTS_ROUTE}/:projectId`}
            />
          </Route>

          <Route element={<PrivateRoute />} path={KEYWORDS_ROUTE}>
            <Route element={<KeywordsPage />} path={KEYWORDS_ROUTE} />
          </Route>

          <Route element={<PrivateRoute />} path={INBOX_ANALYSIS_ROUTE}>
            <Route element={<InboxAnalysis />} path={INBOX_ANALYSIS_ROUTE} />
          </Route>

          <Route element={<PrivateRoute />} path={INBOX_ANALYSIS_DETAIL_ROUTE}>
            <Route
              element={<InboxAnalysisDetail />}
              path={INBOX_ANALYSIS_DETAIL_ROUTE}
            />
          </Route>

          <Route element={<PrivateRoute />} path={CONTENT_ANALYSIS_ROUTE}>
            <Route
              element={<ContentAnalysis />}
              path={CONTENT_ANALYSIS_ROUTE}
            />
          </Route>

          <Route
            element={<PrivateRoute />}
            path={CONTENT_ANALYSIS_DETAIL_ROUTE}
          >
            <Route
              element={<ContentAnalysisDetail />}
              path={CONTENT_ANALYSIS_DETAIL_ROUTE}
            />
          </Route>

          <Route element={<PrivateRoute />} path={REVIEW_ANALYSIS_ROUTE}>
            <Route
              element={<CampaignSelector />}
              path={REVIEW_ANALYSIS_ROUTE}
            />
          </Route>

          <Route
            element={<PrivateRoute />}
            path={REVIEW_ANALYSIS_CAMPAIGN_ROUTE}
          >
            <Route
              element={<ReviewAnalysis />}
              path={REVIEW_ANALYSIS_CAMPAIGN_ROUTE}
            />
          </Route>

          <Route
            element={<PrivateRoute />}
            path={REVIEW_ANALYSIS_BRAND_LEVEL_WIDGETS_ROUTE}
          >
            <Route
              element={<BrandLevelWidgets />}
              path={REVIEW_ANALYSIS_BRAND_LEVEL_WIDGETS_ROUTE}
            />
          </Route>

          <Route
            element={<PrivateRoute />}
            path={REVIEW_ANALYSIS_PLATFORM_DETAILS_ROUTE}
          >
            <Route
              element={<PlatformLevelWidgets />}
              path={REVIEW_ANALYSIS_PLATFORM_DETAILS_ROUTE}
            />
          </Route>
          <Route
            element={<PrivateRoute />}
            path={REVIEW_ANALYSIS_CONNECT_ROUTE}
          >
            <Route
              element={<ConnectPlatformsPage />}
              path={REVIEW_ANALYSIS_CONNECT_ROUTE}
            />
          </Route>
          <Route
            element={<PrivateRoute />}
            path={REVIEW_ANALYSIS_FACILITY_DETAILS_ROUTE}
          >
            <Route
              element={<FacilityLevelWidgets />}
              path={REVIEW_ANALYSIS_FACILITY_DETAILS_ROUTE}
            />
          </Route>

          <Route element={<PrivateRoute />} path={AD_ANALYSIS_OVERVIEW_ROUTE}>
            <Route
              element={<AdAnalysisOverview />}
              path={AD_ANALYSIS_OVERVIEW_ROUTE}
            />
          </Route>

          <Route
            element={<PrivateRoute />}
            path={`${AD_ANALYSIS_LIBRARY_ROUTE}/*`}
          >
            <Route element={<AdLibrary />} index />
            <Route element={<AdLibraryDetails />} path=":pageId" />
          </Route>

          <Route
            element={<PrivateRoute />}
            path={AD_ANALYSIS_PERFORMANCE_ROUTE}
          >
            <Route
              element={<AdPerformance />}
              path={AD_ANALYSIS_PERFORMANCE_ROUTE}
            />
          </Route>

          <Route element={<PrivateRoute />} path={CUSTOM_REPORTING_ROUTE}>
            <Route
              element={<CustomReporting />}
              path={CUSTOM_REPORTING_ROUTE}
            />
          </Route>

          <Route element={<PrivateRoute />} path={NEW_CAMPAIGN_ROUTE}>
            <Route element={<AddNewCampaign />} path={NEW_CAMPAIGN_ROUTE} />
          </Route>

          <Route element={<PrivateRoute />} path={SETTINGS_ROUTE}>
            <Route
              element={<Navigate replace to={SETTINGS_ACCOUNT_ROUTE} />}
              path={SETTINGS_ROUTE}
            />
          </Route>

          <Route element={<PrivateRoute />} path={SETTINGS_ACCOUNT_ROUTE}>
            <Route element={<Settings />} path={SETTINGS_ACCOUNT_ROUTE} />
          </Route>

          <Route element={<PrivateRoute />} path={SETTINGS_SUBSCRIPTION_ROUTE}>
            <Route element={<Settings />} path={SETTINGS_SUBSCRIPTION_ROUTE} />
          </Route>

          <Route
            element={<PrivateRoute />}
            path={SETTINGS_COMPANY_PROFILE_ROUTE}
          >
            <Route
              element={<Settings />}
              path={SETTINGS_COMPANY_PROFILE_ROUTE}
            />
          </Route>

          <Route element={<PrivateRoute />} path={SETTINGS_BILLING_ROUTE}>
            <Route element={<Settings />} path={SETTINGS_BILLING_ROUTE} />
          </Route>

          <Route element={<PrivateRoute />} path={AI_ASSISTANT_ROUTE}>
            <Route element={<AIAssistantPage />} path={AI_ASSISTANT_ROUTE} />
          </Route>

          <Route element={<PrivateRoute />} path={AI_LISTENING_ROUTE}>
            <Route
              element={
                <AiListeningRoute>
                  <AiListeningHomeRedirect />
                </AiListeningRoute>
              }
              path={AI_LISTENING_ROUTE}
            />
            <Route
              element={
                <AiListeningRoute>
                  <BrandProfileLanding />
                </AiListeningRoute>
              }
              path={AI_LISTENING_BRAND_ROUTE}
            />
            <Route
              element={
                <AiListeningRoute>
                  <CreateBrandProfilePage />
                </AiListeningRoute>
              }
              path={AI_LISTENING_BRAND_NEW_ROUTE}
            />
            <Route
              element={
                <AiListeningRoute>
                  <BrandProfilePage />
                </AiListeningRoute>
              }
              path={AI_LISTENING_BRAND_PROFILE_ROUTE}
            />
            <Route
              element={
                <AiListeningRoute>
                  <CompetitorsPage />
                </AiListeningRoute>
              }
              path={AI_LISTENING_COMPETITORS_ROUTE}
            />
            <Route
              element={
                <AiListeningRoute>
                  <CreateCompetitorPage />
                </AiListeningRoute>
              }
              path={AI_LISTENING_COMPETITOR_NEW_ROUTE}
            />
            <Route
              element={
                <AiListeningRoute>
                  <CompetitorsPage />
                </AiListeningRoute>
              }
              path={AI_LISTENING_COMPETITOR_ROUTE}
            />
            <Route
              element={
                <AiListeningRoute>
                  <PromptsPage />
                </AiListeningRoute>
              }
              path={AI_LISTENING_PROMPTS_ROUTE}
            />
            <Route
              element={
                <AiListeningRoute>
                  <PromptDeepDivePage />
                </AiListeningRoute>
              }
              path={AI_LISTENING_PROMPT_DETAILS_ROUTE}
            />
            <Route
              element={
                <AiListeningRoute>
                  <AiListeningNewCampaignRedirect />
                </AiListeningRoute>
              }
              path={AI_LISTENING_NEW_CAMPAIGN_ROUTE}
            />
            <Route
              element={
                <AiListeningRoute>
                  <AiListeningCampaignRedirect />
                </AiListeningRoute>
              }
              path={AI_LISTENING_CAMPAIGN_DETAIL_ROUTE}
            />
            <Route
              element={
                <AiListeningRoute>
                  <AiListeningCampaignRedirect />
                </AiListeningRoute>
              }
              path={AI_LISTENING_DETAILS_ROUTE}
            />
          </Route>

          <Route element={<PrivateRoute />} path={SMART_INSIGHTS_TEST_ROUTE}>
            <Route
              element={<SmartInsightsTest />}
              path={SMART_INSIGHTS_TEST_ROUTE}
            />
          </Route>

          {/* Stripe Checkout redirect targets: bounce into the app with a
              ?checkout=success flag (Layout shows the success banner) instead of
              falling through to the login catch-all. */}
          <Route
            element={
              <Navigate
                replace
                to={`${SETTINGS_SUBSCRIPTION_ROUTE}?checkout=success`}
              />
            }
            path={SUBSCRIPTION_SUCCESS_ROUTE}
          />
          <Route
            element={<Navigate replace to={SETTINGS_SUBSCRIPTION_ROUTE} />}
            path={SUBSCRIPTION_CANCEL_ROUTE}
          />

          <Route element={<Login />} path={LOGIN_ROUTE}></Route>

          <Route element={<SignUp />} path={SIGNUP_ROUTE}></Route>

          <Route element={<Navigate to={LOGIN_ROUTE} />} path="*" />
        </Routes>
      </BrowserRouter>
    </AIAssistantProvider>
  )
}
